<template>
    <layout-header>
        <template #heading>
            <slot name="heading" />
        </template>
        <template v-if="!hideActions" #actions>
            <div v-if="findSlot('before-actions')" class="ml-2">
                <slot name="before-actions" />
            </div>

            <app-export-table
                v-if="headers.length > 0 && exportable"
                class="mr-2"
                :name="exportName"
                :only="exportOnly"
                :on-export-pdf="exportOnExportPdf"
                :on-export-xlsx="exportOnExportXlsx"
                :headers="headers"
                :default-headers="exportHeaders"
                :items="items"
                :request-items="requestItems"
                :filters="filters"
                :cast="cast"
                :disabled="loading"
            />

            <app-table-column-visibility-button
                v-if="!hideColumnVisibility && headers.length > 0 && display === 'table'"
                class="mr-2"
                :value="activeHeaderKeys"
                :headers="headers"
                @input="v => $emit('header-visibility', v)"
            />

            <app-table-display-button
                v-if="!lockDisplay && headers.length > 0 && displayFormats.length > 1"
                class="mr-2"
                :value="display"
                :allowed="displayFormats"
                @input="v => $emit('display', v)"
            />

            <app-table-filter-button
                v-if="!hideFilters && showFilterButton && headers.length > 0"
                :filter-panel-id="filterPanelId"
                :headers="headers"
                :request-items="filterRequestItems || requestItems"
                :disabled="loading"
                @apply-filters="filters => $emit('apply-filters', filters)"
                @clear-filters="() => $emit('clear-filters')"
                @load="() => $emit('loaded-filters')"
            />

            <div v-if="findSlot('actions')" class="mx-2">
                <slot name="actions" />
            </div>
        </template>
    </layout-header>
</template>

<script>
export default {
    name: 'Top',
    components: {
        LayoutHeader: () =>
            import(
                /* webpackChunkName: "layouts" */
                '@/components/layouts/LayoutHeader'
            ),
        AppExportTable: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppExportTable'
            ),
        AppTableColumnVisibilityButton: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/table/ColumnVisibilityButton'
            ),
        AppTableFilterButton: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/table/FilterButton'
            ),
        AppTableDisplayButton: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/table/DisplayButton'
            )
    },
    props: {
        exportable: {
            type: [Boolean, Array, Object],
            default: false
        },
        headers: {
            type: Array,
            default: () => []
        },
        exportHeaders: {
            type: Array,
            default: () => []
        },
        activeHeaderKeys: {
            type: Array,
            default: () => []
        },
        requestItems: {
            type: Function,
            default: null
        },
        filterPanelId: {
            type: String,
            default: null
        },
        filters: {
            type: Object,
            default: () => {}
        },
        filterRequestItems: {
            type: Function,
            default: null
        },
        display: {
            type: String,
            default: 'table' // table, cards, kanban
        },
        displayFormats: {
            type: Array,
            default: () => ['table', 'cards']
        },
        lockDisplay: {
            type: Boolean,
            default: false
        },
        items: {
            type: Array,
            default: null
        },
        hideColumnVisibility: {
            type: Boolean,
            default: false
        },
        hideFilters: {
            type: Boolean,
            default: false
        },
        hideActions: {
            type: Boolean,
            default: false
        },
        cast: {
            type: Function,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        showFilterButton() {
            return !this._.isNil(this._.find(this.headers, ['filterable', true]));
        },
        exportName() {
            if (typeof this.exportable === 'object' && 'name' in this.exportable) {
                return this.exportable.name;
            }

            return null;
        },
        exportOnly() {
            if (Array.isArray(this.exportable)) {
                return this.exportable;
            }

            if (typeof this.exportable === 'object' && 'only' in this.exportable) {
                return this.exportable.only;
            }

            return null;
        },
        exportOnExportPdf() {
            if (typeof this.exportable === 'object' && 'onExportPdf' in this.exportable) {
                return this.exportable.onExportPdf;
            }

            return null;
        },
        exportOnExportXlsx() {
            if (typeof this.exportable === 'object' && 'onExportXlsx' in this.exportable) {
                return this.exportable.onExportXlsx;
            }

            return null;
        }
    }
};
</script>
